


import {
  AutoAwesome as AutoAwesomeIcon,
  AccountCircle as AccountCircleIcon,
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Campaign as CampaignIcon,
  Feedback as FeedbackIcon,
  History as HistoryIcon,
  Home as HomeIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ListAlt as ListAltIcon,
  Logout as LogoutIcon,
  Mail as MailIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// ===== Logo Component =====
const Logo = () => (
  <div className="flex items-center gap-2">
    <img 
      src="https://i.postimg.cc/Rhd5hYcN/halfolog-removebg-preview.png" 
      alt="Influenceso Logo" 
      className="w-10 h-auto"
    />
    <span className="text-white font-semibold text-xl">influenceso</span>
  </div>
);

const Sidebar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // desktop sidebar
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // mobile menu
  const [campaignOpen, setCampaignOpen] = useState(false);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Redirect if no token
  useEffect(() => {
    const token = localStorage.getItem("Brandtoken");
    if (!token) navigate("/BrandLogin");
  }, [navigate]);

  const menus = [
    { name: "Home", link: "/BrandHome", icon: HomeIcon },
    { name: "Add Requirements", link: "/BrandPendingRequest", icon: HourglassEmptyIcon },
    { name: "Invite", link: "/messagebrand", icon: MailIcon },
    { name: "Search", link: "/search", icon: SearchIcon },
    { name: "Plan and list", link: "/plan", icon: ListAltIcon },
  ];

  const campaignMenus = [
    { name: "Create Campaign", link: "/create", icon: AddIcon },
    { name: "Campaign with Ai", link: "/Ai", icon:AutoAwesomeIcon },
    { name: "My Campaigns", link: "/MyCampaigns", icon: PersonIcon },
    { name: "Campaign reports", link: "/campaignreports", icon: HistoryIcon },
  ];

  const logout = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(";").forEach(cookie => {
        const name = cookie.split("=")[0].trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
      if (window.indexedDB) indexedDB.deleteDatabase("your-database-name");
      if (window.caches) caches.keys().then(names => names.forEach(name => caches.delete(name)));
      window.open("https://influenceso.in/", "_blank");
    } catch (err) {
      console.error(err);
      localStorage.clear();
      sessionStorage.clear();
      window.open("https://influenceso.in/", "_blank");
    }
  };

  const handleMenuClick = () => {
    if (isMobile) setMobileMenuOpen(false);
  };

  // === MOBILE VIEW ===
  if (isMobile) {
    return (
      <div className="bg-black text-white fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between p-4">
          <Logo />
          <button
            aria-label="Toggle menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {mobileMenuOpen && (
          <nav className="bg-black border-t border-gray-700">
            <ul className="flex flex-col p-3 space-y-2">
              {menus.map((menu, idx) => (
                <li key={idx} className="hover:bg-gray-800 rounded">
                  <Link to={menu.link} onClick={handleMenuClick} className="flex items-center gap-3 p-2 text-white">
                    <menu.icon className="w-5 h-5" /> {menu.name}
                  </Link>
                </li>
              ))}

              {/* Campaign dropdown */}
              <li>
                <button
                  onClick={() => setCampaignOpen(!campaignOpen)}
                  className="flex items-center gap-3 p-2 w-full text-white hover:bg-gray-800 rounded"
                >
                  <CampaignIcon className="w-5 h-5" />
                  <span>Campaign</span>
                  <ArrowDropDownIcon className={`w-5 h-5 ml-auto transition-transform duration-300 ${campaignOpen ? "rotate-180" : ""}`} />
                </button>
                {campaignOpen && (
                  <ul className="pl-8 mt-1 space-y-1">
                    {campaignMenus.map((cmenu, i) => (
                      <li key={i} className="hover:bg-gray-700 rounded">
                        <Link to={cmenu.link} onClick={handleMenuClick} className="flex items-center gap-3 p-2 text-white">
                          <cmenu.icon className="w-5 h-5" /> {cmenu.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>

              <li className="hover:bg-gray-800 rounded">
                <Link to="/BrandProfile" onClick={handleMenuClick} className="flex items-center gap-3 p-2 text-white">
                  <AccountCircleIcon className="w-5 h-5" /> Profile
                </Link>
              </li>

              <li className="hover:bg-gray-800 rounded">
                <Link to="/complaint" onClick={handleMenuClick} className="flex items-center gap-3 p-2 text-white">
                  <FeedbackIcon className="w-5 h-5" /> Complaint
                </Link>
              </li>

              <li>
                <button onClick={logout} className="flex items-center gap-3 p-2 w-full text-red-500 hover:text-red-600">
                  <LogoutIcon className="w-5 h-5" /> Logout
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    );
  }

  // === DESKTOP VIEW ===
  return (
    <div className="relative flex">
      <div
        className={`${open ? "w-72" : "w-20"} bg-black p-5 pt-8 relative duration-300 transition-all`}
        style={{ height: "100vh" }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <img
          src="https://i.postimg.cc/8P1D6Vq6/left-arrow.jpg"
          alt="Toggle sidebar"
          className={`absolute cursor-pointer -right-3 top-9 w-7 border-dark-purple border-2 rounded-full ${!open && "rotate-180"}`}
          onClick={() => setOpen(!open)}
        />

        <div className="flex gap-x-4 items-center">
          <Logo />
        </div>

        <ul className="pt-6">
          {menus.map((menu, index) => (
            <li key={index} className="hover:bg-gray-800 rounded flex rounded-md p-2 cursor-pointer text-gray-300 text-sm items-center gap-x-4">
              <Link to={menu.link} className="flex items-center gap-3 w-full">
                <menu.icon />
                <span className={`${!open && "opacity-0 translate-x-28 overflow-hidden"}`}>{menu.name}</span>
              </Link>
            </li>
          ))}

          {/* Campaign */}
          <li onClick={() => setCampaignOpen(!campaignOpen)} className="flex rounded-md p-2 cursor-pointer hover:bg-light-white text-gray-300 text-sm items-center gap-x-4 select-none">
            <CampaignIcon />
            <span className={`whitespace-pre duration-500 flex items-center justify-between w-full ${!open && "opacity-0 translate-x-28 overflow-hidden"}`}>
              Campaign <ArrowDropDownIcon className={`transition-transform duration-300 ${campaignOpen ? "rotate-180" : ""}`} />
            </span>
          </li>

          {open && campaignOpen && (
            <ul className="pl-7">
              {campaignMenus.map((cmenu, i) => (
                <li key={i} className="hover:bg-gray-800 rounded flex rounded-md p-2 cursor-pointer text-gray-300 text-sm items-center gap-x-4">
                  <Link to={cmenu.link} className="flex items-center gap-3 w-full">
                    <cmenu.icon /> {cmenu.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <li className="hover:bg-gray-800 rounded flex rounded-md p-2 cursor-pointer text-gray-300 text-sm items-center gap-x-4">
            <Link to="/BrandProfile" className="flex items-center gap-3 w-full">
              <AccountCircleIcon />
              <span className={`${!open && "opacity-0 translate-x-28 overflow-hidden"}`}>Profile</span>
            </Link>
          </li>

          <li className="hover:bg-gray-800 rounded flex rounded-md p-2 cursor-pointer text-gray-300 text-sm items-center gap-x-4">
            <Link to="/complaint" className="flex items-center gap-3 w-full">
              <FeedbackIcon />
              <span className={`${!open && "opacity-0 translate-x-28 overflow-hidden"}`}>Complaint</span>
            </Link>
          </li>

          <li className="hover:bg-gray-800 rounded flex rounded-md p-2 cursor-pointer text-gray-300 text-sm items-center gap-x-4">
            <button onClick={logout} className="flex items-center gap-3 w-full">
              <LogoutIcon />
              <span className={`${!open && "opacity-0 translate-x-28 overflow-hidden"}`}>Logout</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;